/** Sentinel tag name used to filter for blanks (e.g. items without tags). */
const BlankTag = '[blank]';

const blankify = (tagNames) =>
  tagNames && tagNames.length !== 0
    ? tagNames
    : [BlankTag]
;

/**
 * Filters items to those specified by page filters. For use with `withFilters`.
 *
 * @param items [Array<Object>] taggable objects
 * @param filters [Object] the page filters
 * @returns items filtered by tags
 */
const filterByTags = (items, filters) => {
  const tags = filters.tags || [];
  return items.filter(item => tags.every(tag => blankify(item.tag_names).includes(tag)));
};

/**
 * Filters tag objects to those referenced by all taggable items.
 *
 * @param tags [Array<Tag>] tag objects
 * @param items [Array<Object>] taggable items
 * @returns [Array<Tag>] referenced tags
 */
const referencedByAll = (tags, items) => {
  let ref = items.length !== 0
    ? blankify(items[0].tag_names)
    : [];

  items.slice(1).forEach(item => {
    ref = ref.filter(name => blankify(item.tag_names).includes(name));
  });

  return tags.filter(t => ref.includes(t.name));
}

/**
 * Filters tag objects to those referenced by any taggable item.
 *
 * The taggable items must have a `tag_names` property.
 *
 * @param tags [Array<Object>] tag objects
 * @param items [Array<Object>] taggable items
 * @return [Array<Tag>] referenced tags
 */
const referencedByAny = (tags, items) => {
  const ref = new Set();

  items.forEach(item => {
    blankify(item.tag_names).forEach(name => {
      ref.add(name);
    });
  });

  return tags.filter(tag => ref.has(tag.name));
};

/**
 * Filters tags to those that can be bulk added to specified items.
 *
 * These are ones not already tagged to all items.
 *
 * @param tags [Array<Object>] tag objects
 * @param items [Array<Object>] taggable items
 * @return [Array<Tag>] addable tags
 */
const addableTags = (tags, items) => {
  const ref = referencedByAll(tags, items).map(t => t.name);
  return tags.filter(t => !ref.includes(t.name));
};

/**
 * Helper function for filtering by tags and using the "<Filters/>" component.
 *
 * Returns a 2-array. The first element is the array of filtered objects. The second is an array that can
 * be passed as the "fields" prop to the "<Filters/>" component. It will be empty if there are no tags to
 * filter by.
 *
 * @param {Array<Object>} taggables array of taggable objects
 * @param {Object} filters page filters from withFilter HOC
 * @param {Array<Object>} tags tag objects
 * @returns {[undefined,*[]]}
 */
const useTagFilter = (taggables, {filters, tags}) => {
  const filteredTaggables = filterByTags(taggables, filters);

  const usedTags = referencedByAny(
    // Add sentinel tag object for "[blank]"
    [{name: BlankTag}, ...(tags || [])],

    [
      // Add sentinel object with tags from page filters
      ...(filters.hasOwnProperty('tags') ? [{tag_names: filters.tags}] : []),
      ...filteredTaggables
    ]
  );

  const filterFields = [];

  if (usedTags.length !== 0) {
    filterFields.push({solr_prompt: 'tags', display_name: 'Tags', options: usedTags.map(t => t.name)});
  }

  return [filteredTaggables, filterFields];
};

export {
  BlankTag,
  addableTags,
  filterByTags,
  referencedByAll,
  referencedByAny,
  referencedByAny as removableTags,
  useTagFilter
};
